import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	OnDestroy,
	OnInit,
	TemplateRef,
	ViewChild,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import moment from "moment";
import { Columns, Config, DefaultConfig, Pagination } from "ngx-easy-table";
import { Subscription } from "rxjs";
import { CONSTANT } from "src/app/constant";
import { RouteType } from "src/app/models/enum";
import { PaginationDetails } from "src/app/models/models";
import { DateRange } from "src/app/shared/constants/angular-datetime-picker.const";
import { HttpService } from "src/app/shared/services/http.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { RoleService } from "src/app/shared/services/role.service";
import { SharedService } from "src/app/shared/services/shared.service";
import { defaultListDateRange } from "src/app/shared/utils/list-date-filter.utils";
import { parseAocIdsFromQuery } from "src/app/util/aoc-filter-options.utils";
import { FeedbackResponseListItem } from "../feedback.types";

@Component({
	selector: "app-feedback-responses",
	templateUrl: "./feedback-responses.component.html",
	styleUrls: ["./feedback-responses.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class FeedbackResponsesComponent implements OnInit, OnDestroy, AfterViewInit {
	@ViewChild("candidateTpl", { static: true }) candidateTpl!: TemplateRef<any>;
	@ViewChild("idTpl", { static: true }) idTpl!: TemplateRef<any>;
	@ViewChild("formNameTpl", { static: true }) formNameTpl!: TemplateRef<any>;
	@ViewChild("formTypeTpl", { static: true }) formTypeTpl!: TemplateRef<any>;
	@ViewChild("instructorTpl", { static: true }) instructorTpl!: TemplateRef<any>;
	@ViewChild("dateTpl", { static: true }) dateTpl!: TemplateRef<any>;
	@ViewChild("actionTpl", { static: true }) actionTpl!: TemplateRef<any>;

	templateId = "";
	templateName = "Feedback Form";
	isStandalone = false;
	searchQuery = "";
	selectedAocIds: string[] = [];
	columns: Columns[] = [];
	configuration: Config = { ...DefaultConfig };
	responses: FeedbackResponseListItem[] = [];
	pagination: Pagination = {
		limit: CONSTANT.PAGINATION.DEFAULT_SIZE,
		offset: 1,
		count: 1,
	};

	private readonly initialRange = defaultListDateRange();
	startDate = this.initialRange.startDate;
	endDate = this.initialRange.endDate;
	private filterStart = moment(this.startDate).format(CONSTANT.DATE_FORMAT.FILTER_DATE_FORMAT);
	private filterEnd = moment(this.endDate).format(CONSTANT.DATE_FORMAT.FILTER_DATE_FORMAT);
	dateFilterEnabled = false;
	private page = CONSTANT.PAGINATION.DEFAULT_PAGE;
	private size = CONSTANT.PAGINATION.DEFAULT_SIZE;
	private subs = new Subscription();
	private searchTimeout?: ReturnType<typeof setTimeout>;

	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private httpService: HttpService,
		private loaderService: LoaderService,
		private roleService: RoleService,
		private sharedService: SharedService,
		private cdr: ChangeDetectorRef,
	) {}

	ngOnInit(): void {
		this.sharedService.setActivePage(RouteType.FEEDBACK);
		this.initTableConfig();
		this.subs.add(
			this.route.queryParamMap.subscribe((params) => {
				const aocIds = params.getAll("aocIds");
				this.selectedAocIds = aocIds.length ? aocIds : parseAocIdsFromQuery(params.get("aocId"));
			}),
		);
		this.subs.add(
			this.route.paramMap.subscribe((params) => {
				this.templateId = params.get("id") ?? "";
				if (this.templateId) {
					this.loadTemplateName();
					this.loadResponses();
				}
			}),
		);
	}

	ngAfterViewInit(): void {}

	ngOnDestroy(): void {
		this.subs.unsubscribe();
		if (this.searchTimeout) clearTimeout(this.searchTimeout);
	}

	initTableConfig(): void {
		this.configuration = { ...DefaultConfig };
		this.configuration.searchEnabled = false;
		this.configuration.tableLayout.style = "bigger";
		this.configuration.paginationMaxSize = CONSTANT.PAGINATION.MAX_PAGINATION_COUNT;
		this.configuration.rows = this.size;
		this.buildColumns();
	}

	buildColumns(): void {
		const columns: Columns[] = [
			{ key: "candidateName", title: "Candidate Name", cellTemplate: this.candidateTpl, width: "18%" },
			{ key: "candidateStaffNumber", title: "Candidate ID", cellTemplate: this.idTpl, width: "12%" },
		];

		if (!this.isStandalone) {
			columns.push(
				{ key: "formName", title: "Form Name", cellTemplate: this.formNameTpl, width: "15%" },
				{ key: "formTypeLabel", title: "Form Type", cellTemplate: this.formTypeTpl, width: "12%" },
				{ key: "instructorName", title: "Instructor", cellTemplate: this.instructorTpl, width: "15%" },
			);
		}

		columns.push(
			{ key: "submittedAt", title: "Date Submitted", cellTemplate: this.dateTpl, width: "15%" },
			{ key: "", title: "Action", cellTemplate: this.actionTpl, width: "10%" },
		);

		this.columns = columns;
	}

	loadTemplateName(): void {
		this.subs.add(
			this.httpService.getFeedbackTemplate(this.templateId, this.buildScopeParams()).subscribe({
				next: (resp) => {
					const data = resp?.data ?? resp;
					this.templateName = data?.name ?? "Feedback Form";
					this.isStandalone = data?.isStandalone === true;
					this.buildColumns();
					this.cdr.detectChanges();
				},
			}),
		);
	}

	loadResponses(): void {
		this.loaderService.setLoading(true);
		this.subs.add(
			this.httpService
				.listFeedbackResponses(this.templateId, {
					page: this.page,
					size: this.size,
					search: this.searchQuery.trim() || undefined,
					...(this.dateFilterEnabled ? { startDate: this.filterStart, endDate: this.filterEnd } : {}),
					...this.buildScopeParams(),
				})
				.subscribe({
					next: (resp) => {
						const payload = resp?.data ?? resp;
						this.responses = payload?.data ?? [];
						const total = payload?.total ?? 0;
						const paginationDetails = this.calculatePages(total, this.size);
						this.pagination = {
							limit: this.size,
							offset: this.page,
							count: this.size * paginationDetails.totalPages,
						};
						this.loaderService.setLoading(false);
						this.cdr.detectChanges();
					},
					error: () => this.loaderService.setLoading(false),
				}),
		);
	}

	handleDateRangeChange(dateRange: DateRange): void {
		if (dateRange?.enabled === false) {
			this.dateFilterEnabled = false;
		} else if (dateRange?.startDate && dateRange?.endDate) {
			this.dateFilterEnabled = true;
			this.startDate = dateRange.startDate;
			this.endDate = dateRange.endDate;
			this.filterStart = moment(dateRange.startDate).format(CONSTANT.DATE_FORMAT.FILTER_DATE_FORMAT);
			this.filterEnd = moment(dateRange.endDate).format(CONSTANT.DATE_FORMAT.FILTER_DATE_FORMAT);
		} else {
			return;
		}

		this.page = 1;
		this.loadResponses();
	}

	onSearch(): void {
		if (this.searchTimeout) clearTimeout(this.searchTimeout);
		this.searchTimeout = setTimeout(() => {
			this.page = 1;
			this.loadResponses();
		}, 300);
	}

	onTableChanged(event: any): void {
		if (event.event !== "onPagination") return;
		this.page = event.value.page;
		this.size = event.value.limit;
		this.configuration.rows = this.size;
		this.loadResponses();
	}

	viewResponse(submissionId: string): void {
		const queryParams: { aocIds?: string[] } = {};
		if (this.roleService.isGroupAdmin && this.selectedAocIds.length) {
			queryParams.aocIds = this.selectedAocIds;
		}
		this.router.navigate([RouteType.FEEDBACK, this.templateId, "responses", submissionId], { queryParams });
	}

	backToList(): void {
		this.router.navigate([RouteType.FEEDBACK]);
	}

	formatDate(value?: string): string {
		if (!value) return "-";
		return moment(value).format(CONSTANT.DATE_FORMAT.TABLE_DATE_FORMAT);
	}

	calculatePages(totalDataCount: number, rowsPerPage: number): PaginationDetails {
		const totalPages = Math.ceil(totalDataCount / rowsPerPage) || 1;
		const lastPageSize = totalDataCount % rowsPerPage;
		return { totalPages, lastPageSize };
	}

	private buildScopeParams(): { role: string; aocIds?: string[] } {
		const params: { role: string; aocIds?: string[] } = {
			role: this.roleService.getUserRole(),
		};
		if (this.roleService.isGroupAdmin && this.selectedAocIds.length) {
			params.aocIds = this.selectedAocIds;
		}
		return params;
	}
}
